import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantWhitepaperComponent } from '../relevant-whitepaper/relevant-whitepaper.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ResourcesService } from '../../../services/resources.service';
import { HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'app-autonomous-machine-revolution',
  imports: [CommonModule, RelevantWhitepaperComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './autonomous-machine-revolution.component.html',
  styleUrl: './autonomous-machine-revolution.component.scss'
})
export class AutonomousMachineRevolutionComponent implements OnInit, OnDestroy {
  isModalOpen: boolean = false;
  downloadForm: FormGroup;
  isSubmitting: boolean = false;
  submitSuccess: boolean = false;
  errorMessage: string = '';
  private jsonLdScript: any;

  // IMPORTANT: This string matches the Key added to the Dictionary in EmailService.cs
  readonly whitepaperTitle = "Agentic AI in SDLC";

  // Path for direct browser download (optional, if you want them to get it immediately + email)
  readonly pdfPath = "../../../../assets/resources/whitepaper/agentic-ai-in-sdlc/agentic-ai-in-sdlc.pdf";

  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+( [A-Za-z]+)*$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const autonomousMachineSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-white-papers/autonomous-machine-revolution",
          "url": "https://www.kalpitatechnologies.com/kalpita-white-papers/autonomous-machine-revolution",
          "name": "The Autonomous Machine Revolution & Rise of Machine Agency — White Paper | Kalpita Technologies",
          "description": "Explore how the autonomous machine revolution is shifting machines from programmed tools to independent agents capable of perceiving, deciding, transacting, and collaborating within M2M ecosystems.",
          "inLanguage": "en-US",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".softwaredev-heading", ".section-title", ".takeaway-item p:first-of-type"]
          },
          "datePublished": "2026-01-15",
          "dateModified": "2026-01-15",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-white-papers/autonomous-machine-revolution#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-white-papers", "name": "White Papers" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-white-papers/autonomous-machine-revolution", "name": "Autonomous Machine Revolution" } }
          ]
        },
        {
          "@type": "TechArticle",
          "@id": "https://www.kalpitatechnologies.com/kalpita-white-papers/autonomous-machine-revolution#article",
          "headline": "The Autonomous Machine Revolution and the Rise of Machine Agency",
          "articleBody": "The autonomous machine revolution marks a shift from machines as programmed tools to independent agents capable of perceiving environments, making decisions, and transacting autonomously within M2M ecosystems...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2026-01-15",
          "proficiencyLevel": "Expert"
        },
        {
          "@type": "HowTo",
          "name": "How to Prepare Your Organization for the Autonomous Machine Revolution",
          "step": [
            { "@type": "HowToStep", "text": "Understand the Shift from Automation to Autonomy." },
            { "@type": "HowToStep", "text": "Build the Enabling Technology Stack (IoT, 5G, AI, Blockchain)." },
            { "@type": "HowToStep", "text": "Pilot Autonomous Deployments (Robotics, Logistics, Smart Factories)." },
            { "@type": "HowToStep", "text": "Establish Governance and Ethical Frameworks." },
            { "@type": "HowToStep", "text": "Transition Workforce Roles and Upskill Teams." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What are the benefits of autonomous machines?",
              "acceptedAnswer": { "@type": "Answer", "text": "Autonomous machines deliver 20-30% cost savings, 24/7 productivity, and mass customization." }
            },
            {
              "@type": "Question",
              "name": "What role do humans play in the autonomous era?",
              "acceptedAnswer": { "@type": "Answer", "text": "Humans transition from operators to strategic architects focusing on intent and oversight." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(autonomousMachineSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }
  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';

      // Construct Payload matching C# WhitepaperDownloadForm
      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        WhitepaperTitle: this.whitepaperTitle
      };

      this.resourcesService.sendWhitepaperEmail(payload).subscribe({
        next: (response: any) => {
          // Check backend response structure. Usually returns { IsError: false, data: true }
          if (response && !response.IsError) {
            this.handleSuccess();
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message || 'Failed to send email. Please try again.';
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error sending email:', error);
          this.errorMessage = 'Server error. Please check your internet connection.';
        }
      });
    } else {
      this.downloadForm.markAllAsTouched();
    }
  }

  handleSuccess() {
    this.isSubmitting = false;
    this.submitSuccess = true;

    // OPTIONAL: Trigger automatic browser download alongside email
    // this.triggerFileDownload(); 

    // Close modal after 4 seconds
    setTimeout(() => {
      this.closeModal();
    }, 4000);
  }

  triggerFileDownload() {
    const link = document.createElement('a');
    link.href = this.pdfPath;
    link.download = 'rise-of-ai-agent.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
